import { useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { TextFormatButtons, TokenInsertButtons, appendToken } from './tokenTools';
import IconClassPicker from './IconClassPicker';

const API_BASE = process.env.REACT_APP_URL || 'http://localhost:3501';

function usePageBuilderPages() {
  const [pages, setPages] = useState([]);

  useEffect(() => {
    let mounted = true;

    const loadPages = async () => {
      try {
        const response = await fetch(`${API_BASE}/pagebuilder/pages`, {
          headers: { 'Content-Type': 'application/json' },
        });
        if (!response.ok) return;

        const data = await response.json();
        if (!mounted || !Array.isArray(data)) return;
        setPages(data);
      } catch {
        // Ignore page link helper failures; manual URLs still work.
      }
    };

    loadPages();
    return () => {
      mounted = false;
    };
  }, []);

  return useMemo(
    () => pages.map((page) => ({ id: page.id, label: page.title || page.name || page.id })),
    [pages]
  );
}

const subItemMove = (items, id, dir) => {
  const idx = items.findIndex((i) => i.id === id);
  const ni = idx + dir;
  if (ni < 0 || ni >= items.length) return items;
  const next = [...items];
  [next[idx], next[ni]] = [next[ni], next[idx]];
  return next;
};

export default function NavigationEditor({ type, content, update }) {
  const [focusedField, setFocusedField] = useState('label');
  const pageOptions = usePageBuilderPages();
  const buttonLabelInputRef = useRef(null);
  const buttonLabelSelectionRef = useRef({ start: null, end: null });

  const captureButtonLabelSelection = (event) => {
    const target = event?.target;
    if (!target) return;
    buttonLabelSelectionRef.current = {
      start: target.selectionStart,
      end: target.selectionEnd,
    };
  };

  const insertIconClassIntoButtonLabel = (iconClass) => {
    if (!iconClass) return;

    const currentLabel = content.label || '';
    const iconMarkup = `<i class="${iconClass}"></i>`;
    const { start, end } = buttonLabelSelectionRef.current || {};
    const hasSelection = Number.isInteger(start) && Number.isInteger(end);

    if (!hasSelection) {
      const spacer = currentLabel && !/\s$/.test(currentLabel) ? ' ' : '';
      update('label', `${currentLabel}${spacer}${iconMarkup}`);
      return;
    }

    const prefix = currentLabel.slice(0, start);
    const suffix = currentLabel.slice(end);
    const needsLeadingSpace = prefix && !/\s$/.test(prefix);
    const needsTrailingSpace = suffix && !/^\s/.test(suffix);
    const inserted = `${needsLeadingSpace ? ' ' : ''}${iconMarkup}${needsTrailingSpace ? ' ' : ''}`;
    const nextLabel = `${prefix}${inserted}${suffix}`;
    const caretPos = prefix.length + inserted.length;

    update('label', nextLabel);

    window.requestAnimationFrame(() => {
      const input = buttonLabelInputRef.current;
      if (!input) return;
      input.focus();
      input.setSelectionRange(caretPos, caretPos);
      buttonLabelSelectionRef.current = { start: caretPos, end: caretPos };
    });
  };

  const insertToken = (token) => {
    if (focusedField === 'url') {
      update('url', appendToken(content.url || '', token));
      return;
    }
    update('label', appendToken(content.label || '', token));
  };

  switch (type) {

    case 'navMenu': {
      const items = content.items || [];
      const addItem = () => update('items', [...items, { id: `nm-${Date.now()}`, label: '', url: '', openNewTab: false }]);
      const removeItem = (iid) => update('items', items.filter((i) => i.id !== iid));
      const updateItem = (iid, field, val) => update('items', items.map((i) => i.id === iid ? { ...i, [field]: val } : i));
      const moveItem = (iid, dir) => update('items', subItemMove(items, iid, dir));
      return (
        <div className="pagebuilder-editor-fields">
          <TextFormatButtons />
          <div className="pagebuilder-sublist-header">
            <span className="pagebuilder-sublist-title">Menu Items ({items.length})</span>
            <button type="button" className="pagebuilder-add-btn pagebuilder-add-btn--sm" onClick={addItem}>
              <i className="bi bi-plus-lg" aria-hidden="true" /> Add Item
            </button>
          </div>
          {items.length === 0 && <p className="pagebuilder-sublist-empty">No menu items yet.</p>}
          {items.map((item, idx) => (
            <div key={item.id} className="pagebuilder-sublist-item">
              <div className="pagebuilder-sublist-item-header">
                <span className="pagebuilder-sublist-index">#{idx + 1}</span>
                <div className="pagebuilder-block-actions">
                  <button type="button" className="pagebuilder-block-action-btn" onClick={() => moveItem(item.id, -1)} disabled={idx === 0} title="Move up"><i className="bi bi-arrow-up" /></button>
                  <button type="button" className="pagebuilder-block-action-btn" onClick={() => moveItem(item.id, 1)} disabled={idx === items.length - 1} title="Move down"><i className="bi bi-arrow-down" /></button>
                  <button type="button" className="pagebuilder-block-action-btn pagebuilder-block-action-btn--danger" onClick={() => removeItem(item.id)} title="Remove"><i className="bi bi-trash" /></button>
                </div>
              </div>
              <div className="pagebuilder-field-row mb-2">
                <Form.Group style={{ flex: 1 }}>
                  <Form.Label>Label</Form.Label>
                  <Form.Control value={item.label} onChange={(e) => updateItem(item.id, 'label', e.target.value)} placeholder="Menu label…" />
                </Form.Group>
                <Form.Group style={{ flex: 1 }}>
                  <Form.Label>URL</Form.Label>
                  <Form.Control value={item.url} onChange={(e) => updateItem(item.id, 'url', e.target.value)} placeholder="/page or https://…" />
                </Form.Group>
              </div>
              <div className="pagebuilder-field-row mb-2">
                <Form.Group style={{ flex: 1 }}>
                  <Form.Label>Link to Page Builder Page</Form.Label>
                  <Form.Select
                    value={typeof item.url === 'string' && item.url.startsWith('#pb-page:') ? item.url.slice('#pb-page:'.length) : ''}
                    onChange={(e) => {
                      const pageId = e.target.value;
                      if (!pageId) return;
                      update('items', items.map((i) => i.id === item.id ? { ...i, url: `#pb-page:${pageId}`, openNewTab: false } : i));
                    }}
                  >
                    <option value="">Select page...</option>
                    {pageOptions.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
                  </Form.Select>
                </Form.Group>
              </div>
              <Form.Check
                type="checkbox"
                label="Open in new tab"
                checked={!!item.openNewTab}
                onChange={(e) => updateItem(item.id, 'openNewTab', e.target.checked)}
              />
            </div>
          ))}
        </div>
      );
    }

    case 'submenu': {
      const items = content.items || [];
      const addItem = () => update('items', [...items, { id: `sm-${Date.now()}`, label: '', url: '' }]);
      const removeItem = (iid) => update('items', items.filter((i) => i.id !== iid));
      const updateItem = (iid, field, val) => update('items', items.map((i) => i.id === iid ? { ...i, [field]: val } : i));
      return (
        <div className="pagebuilder-editor-fields">
          <TextFormatButtons />
          <div className="pagebuilder-field-row mb-3">
            <Form.Group style={{ flex: 1 }}>
              <Form.Label>Parent Label</Form.Label>
              <Form.Control value={content.parentLabel || ''} onChange={(e) => update('parentLabel', e.target.value)} placeholder="Category name…" />
            </Form.Group>
            <Form.Group style={{ flex: 1 }}>
              <Form.Label>Parent URL</Form.Label>
              <Form.Control value={content.parentUrl || ''} onChange={(e) => update('parentUrl', e.target.value)} placeholder="/category or #" />
            </Form.Group>
          </div>
          <Form.Group className="mb-3">
            <Form.Label>Parent Link to Page Builder Page</Form.Label>
            <Form.Select
              value={typeof content.parentUrl === 'string' && content.parentUrl.startsWith('#pb-page:') ? content.parentUrl.slice('#pb-page:'.length) : ''}
              onChange={(e) => {
                const pageId = e.target.value;
                if (!pageId) return;
                update('parentUrl', `#pb-page:${pageId}`);
              }}
            >
              <option value="">Select page...</option>
              {pageOptions.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
            </Form.Select>
          </Form.Group>
          <div className="pagebuilder-sublist-header">
            <span className="pagebuilder-sublist-title">Sub-items ({items.length})</span>
            <button type="button" className="pagebuilder-add-btn pagebuilder-add-btn--sm" onClick={addItem}>
              <i className="bi bi-plus-lg" aria-hidden="true" /> Add Sub-item
            </button>
          </div>
          {items.length === 0 && <p className="pagebuilder-sublist-empty">No sub-items yet.</p>}
          {items.map((item, idx) => (
            <div key={item.id} className="pagebuilder-sublist-item">
              <div className="pagebuilder-field-row mb-2">
                <Form.Control style={{ flex: 1 }} value={item.label} onChange={(e) => updateItem(item.id, 'label', e.target.value)} placeholder={`Sub-item ${idx + 1} label…`} />
                <Form.Control style={{ flex: 1 }} value={item.url} onChange={(e) => updateItem(item.id, 'url', e.target.value)} placeholder="URL…" />
                <button type="button" className="pagebuilder-block-action-btn pagebuilder-block-action-btn--danger" onClick={() => removeItem(item.id)} title="Remove"><i className="bi bi-trash" /></button>
              </div>
              <Form.Group>
                <Form.Label>Link to Page Builder Page</Form.Label>
                <Form.Select
                  value={typeof item.url === 'string' && item.url.startsWith('#pb-page:') ? item.url.slice('#pb-page:'.length) : ''}
                  onChange={(e) => {
                    const pageId = e.target.value;
                    if (!pageId) return;
                    updateItem(item.id, 'url', `#pb-page:${pageId}`);
                  }}
                >
                  <option value="">Select page...</option>
                  {pageOptions.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
                </Form.Select>
              </Form.Group>
            </div>
          ))}
        </div>
      );
    }

    case 'breadcrumbs': {
      const items = content.items || [];
      const addItem = () => update('items', [...items, { id: `bc-${Date.now()}`, label: '', url: '', icon: '' }]);
      const removeItem = (iid) => update('items', items.filter((i) => i.id !== iid));
      const updateItem = (iid, field, val) => update('items', items.map((i) => i.id === iid ? { ...i, [field]: val } : i));
      return (
        <div className="pagebuilder-editor-fields">
          <TextFormatButtons />
          <p className="pagebuilder-divider-note mb-3">The last item is the current page (leave URL empty). Items are displayed left → right.</p>
          <div className="pagebuilder-sublist-header">
            <span className="pagebuilder-sublist-title">Breadcrumb Items ({items.length})</span>
            <button type="button" className="pagebuilder-add-btn pagebuilder-add-btn--sm" onClick={addItem}>
              <i className="bi bi-plus-lg" aria-hidden="true" /> Add Crumb
            </button>
          </div>
          {items.map((item, idx) => (
            <div key={item.id} className="pagebuilder-sublist-item">
              <div className="pagebuilder-sublist-item pagebuilder-sublist-item--row" style={{ padding: 0, border: 0 }}>
                <Form.Control style={{ flex: 1 }} value={item.label} onChange={(e) => updateItem(item.id, 'label', e.target.value)} placeholder={`Label ${idx + 1}…`} />
                <Form.Control style={{ flex: 1 }} value={item.url} onChange={(e) => updateItem(item.id, 'url', e.target.value)} placeholder="URL (empty = current page)" />
                <button type="button" className="pagebuilder-block-action-btn pagebuilder-block-action-btn--danger" onClick={() => removeItem(item.id)} title="Remove"><i className="bi bi-trash" /></button>
              </div>
              <IconClassPicker
                value={item.icon || ''}
                label="Breadcrumb Icon (optional)"
                placeholder="Search breadcrumb icon..."
                maxRows={2}
                onChange={(nextIconClass) => updateItem(item.id, 'icon', nextIconClass)}
              />
              <Form.Group>
                <Form.Label>Link to Page Builder Page</Form.Label>
                <Form.Select
                  value={typeof item.url === 'string' && item.url.startsWith('#pb-page:') ? item.url.slice('#pb-page:'.length) : ''}
                  onChange={(e) => {
                    const pageId = e.target.value;
                    if (!pageId) return;
                    updateItem(item.id, 'url', `#pb-page:${pageId}`);
                  }}
                >
                  <option value="">Select page...</option>
                  {pageOptions.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
                </Form.Select>
              </Form.Group>
            </div>
          ))}
        </div>
      );
    }

    case 'footer': {
      const cols = content.cols || [];
      const addCol = () => update('cols', [...cols, { id: `fc-${Date.now()}`, title: '', links: '' }]);
      const removeCol = (cid) => update('cols', cols.filter((c) => c.id !== cid));
      const updateCol = (cid, field, val) => update('cols', cols.map((c) => c.id === cid ? { ...c, [field]: val } : c));
      return (
        <div className="pagebuilder-editor-fields">
          <TextFormatButtons />
          <Form.Group className="mb-3">
            <Form.Label>Copyright text</Form.Label>
            <Form.Control value={content.copyright || ''} onChange={(e) => update('copyright', e.target.value)} placeholder="© 2024 Club Name" />
          </Form.Group>
          <div className="pagebuilder-sublist-header">
            <span className="pagebuilder-sublist-title">Columns ({cols.length})</span>
            <button type="button" className="pagebuilder-add-btn pagebuilder-add-btn--sm" onClick={addCol}>
              <i className="bi bi-plus-lg" aria-hidden="true" /> Add Column
            </button>
          </div>
          {cols.map((col, idx) => (
            <div key={col.id} className="pagebuilder-sublist-item">
              <div className="pagebuilder-sublist-item-header">
                <span className="pagebuilder-sublist-index">Column {idx + 1}</span>
                <button type="button" className="pagebuilder-block-action-btn pagebuilder-block-action-btn--danger" onClick={() => removeCol(col.id)} title="Remove"><i className="bi bi-trash" /></button>
              </div>
              <Form.Group className="mb-2">
                <Form.Label>Column Title</Form.Label>
                <Form.Control value={col.title} onChange={(e) => updateCol(col.id, 'title', e.target.value)} placeholder="Quick Links, Contact, etc." />
              </Form.Group>
              <Form.Group>
                <Form.Label>Links (one per line — <code>Label | URL</code>)</Form.Label>
                <Form.Control as="textarea" rows={4} value={col.links} onChange={(e) => updateCol(col.id, 'links', e.target.value)} placeholder={'Home | /\nAbout | /about'} />
              </Form.Group>
            </div>
          ))}
        </div>
      );
    }

    case 'button':
      return (
        <div className="pagebuilder-editor-fields">
          <Form.Group className="mb-3">
            <Form.Label>Label</Form.Label>
            <Form.Control
              ref={buttonLabelInputRef}
              value={content.label || ''}
              onChange={(e) => update('label', e.target.value)}
              onFocus={(e) => {
                setFocusedField('label');
                captureButtonLabelSelection(e);
              }}
              onClick={captureButtonLabelSelection}
              onSelect={captureButtonLabelSelection}
              onKeyUp={captureButtonLabelSelection}
              placeholder="Button text…"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>URL</Form.Label>
            <Form.Control
              value={content.url || ''}
              onChange={(e) => update('url', e.target.value)}
              onFocus={() => setFocusedField('url')}
              placeholder="https://… or /page"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Link to Page Builder Page</Form.Label>
            <Form.Select
              value={typeof content.url === 'string' && content.url.startsWith('#pb-page:') ? content.url.slice('#pb-page:'.length) : ''}
              onChange={(e) => {
                const pageId = e.target.value;
                if (!pageId) return;
                update('url', `#pb-page:${pageId}`);
              }}
            >
              <option value="">Select page...</option>
              {pageOptions.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
            </Form.Select>
          </Form.Group>
          <div className="pagebuilder-field-row mb-3">
            <Form.Group style={{ flex: 1 }}>
              <Form.Label>Variant</Form.Label>
              <Form.Select value={content.variant || 'primary'} onChange={(e) => update('variant', e.target.value)}>
                <option value="primary">Primary</option>
                <option value="secondary">Secondary</option>
                <option value="success">Success</option>
                <option value="danger">Danger</option>
                <option value="warning">Warning</option>
                <option value="outline">Outline</option>
              </Form.Select>
            </Form.Group>
            <Form.Group style={{ flex: 1 }}>
              <Form.Label>Size</Form.Label>
              <Form.Select value={content.size || 'md'} onChange={(e) => update('size', e.target.value)}>
                <option value="sm">Small</option>
                <option value="md">Medium</option>
                <option value="lg">Large</option>
              </Form.Select>
            </Form.Group>
          </div>
          <IconClassPicker
            value={content.icon || ''}
            onChange={(nextIconClass) => {
              update('icon', nextIconClass);
              insertIconClassIntoButtonLabel(nextIconClass);
            }}
            maxRows={2}
          />
          <TokenInsertButtons onInsert={insertToken} />
            <Form.Check
              className="align-self-end pb-2"
              type="checkbox"
              label="Open in new tab"
              checked={!!content.openNewTab}
              onChange={(e) => update('openNewTab', e.target.checked)}
            />
        </div>
      );

    default:
      return null;
  }
}
